import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { HTTPStatusError } from '../lib/chat-api';
import { completeTelegramConnect } from '../lib/telegram';

const telegramConnectMessageType = 'cueboard-telegram-connect-result';

type CallbackStatus = 'loading' | 'success' | 'error';

function readProviderError(searchParams: URLSearchParams): string | null {
  const errorDescription = searchParams.get('error_description')?.trim();
  if (errorDescription) {
    return errorDescription;
  }
  const error = searchParams.get('error')?.trim();
  if (error) {
    return `Telegram connection failed: ${error}`;
  }
  return null;
}

function readHandoffScheme(stateToken: string): string | null {
  const parts = stateToken.trim().split('.', 2);
  if (parts.length !== 2 || !parts[1]) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(padded)) as { s?: unknown };
    const scheme = typeof payload.s === 'string' ? payload.s.trim() : '';
    if (
      scheme === 'bridge' ||
      scheme === 'bridge-dev' ||
      scheme === 'bridge-local' ||
      scheme === 'bridge-debug-local'
    ) {
      return scheme;
    }
  } catch {
    return null;
  }

  return null;
}

function redirectToApp(
  scheme: string,
  status: 'success' | 'error',
  message: string
) {
  const query = new URLSearchParams({ status, message });
  window.location.replace(
    `${scheme}://integration/telegram?${query.toString()}`
  );
}

export function TelegramConnectCallbackPage() {
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [message, setMessage] = useState('Finishing Telegram connection…');

  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const notifyOpener = (payload: Record<string, unknown>) => {
      if (!window.opener) {
        return;
      }
      window.opener.postMessage(
        { type: telegramConnectMessageType, ...payload },
        window.location.origin
      );
    };

    const closeSoon = () => {
      window.setTimeout(() => window.close(), 150);
    };

    const finishWithError = (text: string) => {
      if (cancelled) {
        return;
      }
      const scheme = readHandoffScheme(searchParams.get('state')?.trim() ?? '');
      if (scheme) {
        redirectToApp(scheme, 'error', text);
        return;
      }
      setStatus('error');
      setMessage(text);
      notifyOpener({ ok: false, message: text });
    };

    const finishWithSuccess = async () => {
      const providerError = readProviderError(searchParams);
      if (providerError) {
        finishWithError(providerError);
        return;
      }

      const code = searchParams.get('code')?.trim() ?? '';
      const state = searchParams.get('state')?.trim() ?? '';
      if (!code || !state) {
        finishWithError(
          'Telegram did not send everything Bridge needs. Please return to Bridge and try again.'
        );
        return;
      }

      try {
        const result = await completeTelegramConnect(code, state);
        if (cancelled) {
          return;
        }

        if (result.redirect_url?.trim()) {
          window.location.replace(result.redirect_url);
          return;
        }

        const successMessage =
          result.message?.trim() || 'Telegram connected successfully.';
        setStatus('success');
        setMessage(successMessage);
        notifyOpener({
          ok: true,
          message: successMessage,
          telegram_user_id: result.telegram_user_id,
          telegram_username: result.telegram_username,
        });
        closeSoon();
      } catch (error) {
        if (cancelled) {
          return;
        }
        let text = 'Failed to complete Telegram connection.';
        if (error instanceof HTTPStatusError) {
          text = error.message;
        } else if (error instanceof Error && error.message.trim()) {
          text = error.message;
        }
        finishWithError(text);
      }
    };

    void finishWithSuccess();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md rounded-3xl border border-border/70 bg-card/80 p-8 shadow-sm backdrop-blur">
        <div className="flex items-center gap-3">
          {status === 'loading' ? (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          ) : status === 'success' ? (
            <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-400" />
          ) : (
            <XCircle className="size-5 text-rose-600 dark:text-rose-400" />
          )}
          <div className="text-lg font-medium">
            {status === 'loading'
              ? 'Connecting Telegram'
              : status === 'success'
                ? 'Telegram connected'
                : 'Telegram connection failed'}
          </div>
        </div>
        <p className="mt-4 text-sm leading-6 text-muted-foreground">
          {message}
        </p>
        {status !== 'loading' ? (
          <p className="mt-6 text-xs text-muted-foreground">
            You can close this window if it does not close automatically.
          </p>
        ) : null}
      </div>
    </div>
  );
}
